/* eslint-disable max-len */
import { useTranslation } from 'react-i18next'
import { matchPath, useLocation } from 'react-router-dom'

import { useProject } from '@hooks/project/project.hook'

import { SCROLL_CONTAINER_CALENDAR } from '@utils/constants/pdf-export-ids'

import PdfIcon from '@assets/images/pdf-icon.svg'

import { DropDownFormExport } from '@modules/project-forms/components/DropDownFormExport'
import { DropDownFormsExport } from '@modules/project-forms/components/DropDownFormsExport'
import { DropDownExportObservations } from '@modules/project-observations/components/DropDownExportObservations'
import { DropDownExportPlannings } from '@modules/project-planning/components/Planning/components/DropDownExportPlannings'

interface Props {
  projectId: string
}

export const ProjectActions: React.FC<Props> = ({ projectId }: Props) => {
  const { i18n } = useTranslation()
  const { pathname } = useLocation()

  const planningPath = matchPath(`/${i18n.language}/projects/${projectId}/planning`, pathname)
  const formMatch = matchPath(`/${i18n.language}/projects/${projectId}/forms/details/:formId`, pathname)
  const observationsMatch = matchPath(`/${i18n.language}/projects/${projectId}/observations`, pathname)
  const formsMatch = matchPath(`/${i18n.language}/projects/${projectId}/forms/`, pathname)

  if (planningPath) return <ExportPlanning projectId={projectId} />
  if (formMatch) return <ExportForm formId={formMatch?.params.formId || ''} />
  if (observationsMatch) return <ExportToFile projectId={projectId} />
  if (formsMatch) return <ExportForms projectId={projectId} />
}

type ExportPlanningProps = {
  projectId: string
}

const ExportPlanning: React.FC<ExportPlanningProps> = (props: ExportPlanningProps) => {
  const { projectId } = props
  const { data } = useProject(projectId)
  const { t } = useTranslation()
  return (
    <DropDownExportPlannings
      fileName={data?.project?.name ?? 'calendar'}
      elementId={SCROLL_CONTAINER_CALENDAR}
      exportOptions={[
        {
          id: 'simple-portrait',
          label: t('modules_project-settings_components_Planning_export_simple_portrait'),
          icon: PdfIcon,
        },
        {
          id: 'simple-landscape',
          label: t('modules_project-settings_components_Planning_export_simple_landscape'),
          icon: PdfIcon,
        },
        { id: 'full', label: t('modules_project-settings_components_Planning_export_full'), icon: PdfIcon },
      ]}
      projectId={projectId}
    />
  )
}

type ExportFormProps = {
  formId: string
}

const ExportForm: React.FC<ExportFormProps> = (props: ExportFormProps) => {
  const { formId } = props

  if (!formId) return null

  return <DropDownFormExport formId={formId} />
}

type ExportFormsProps = {
  projectId: string
}

const ExportForms: React.FC<ExportFormsProps> = (props: ExportFormsProps) => {
  const { projectId } = props

  if (!projectId) return null

  return <DropDownFormsExport projectId={projectId} />
}

type ExportObservationsProps = {
  projectId: string
}

const ExportToFile: React.FC<ExportObservationsProps> = (props: ExportObservationsProps) => {
  const { projectId } = props

  return <DropDownExportObservations projectId={projectId} />
}
